import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  gridClasses,
  GridColDef,
} from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import * as yup from 'yup';
import {
  ConfirmModal,
  Form,
  PageComponent,
  RenderFuncProps,
  SelectField,
  TextField,
  tableStyleHideSeparator,
  useNotification,
  useGridApiRefKeepingPage,
} from '@dispatch/web/ui';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  groupingColDef,
  additionalResourcesSum,
  openSlotsSum,
  openSlotsSumClaimOwner,
  openSlotsSumVirtual,
  columnGroupingModel,
  additionalResourcesClaimOwnerSum,
  additionalResourcesVirtualSum,
  activeResourcesFieldSum,
  activeResourcesClaimOwnerSum,
  activeResourcesVirtualSum,
} from '../common/grid_helpers';
import { useColumns } from '../common/useColumns';
import { useFormatData } from '../common/useFormatData';
import styled from '@emotion/styled';
import {
  AdditionalResource,
  CapacityDashboard_Request,
  CapacityResponseSchema,
  ReportFormat,
  ReportType,
  ResourceSchema,
  TaskPeril,
} from '@dispatch/shared/types';
import React, { useMemo, useState } from 'react';
import { useFriendlyNameMapper } from '../common/useFriendlyNameMapper';
import { isEmpty } from 'lodash';
import { format, parseISO } from 'date-fns';
import { z } from 'zod';
import { ClaimOwnerToolTip } from './ClaimOwnerToolTip';
import { FileDownload } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useExportReport } from '@dispatch/shared/hooks';
import { useCurrentUser } from '@dispatch/web/auth';

const StyledGridOverlay = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
}));

const getDateData = (data: any) => {
  if (!data) return;
  const resourceData = ResourceSchema.parse(data);

  resourceData.dateData.forEach((dateData) => {
    data[dateData.date] = {
      info: `${dateData.capacity}(${dateData.totalTaskCount})`,
      hoverData: dateData.hoverData,
    };
  });
};

interface CapacityDashboardProps {
  data?: any;
  capacityType?: string;
  unassignedTasks?: AdditionalResource[];
  loading?: boolean;
  openState?: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
  filters?: CapacityDashboard_Request | null;
  companyTeamsLookup?: { [key: string]: string };
  perils?: TaskPeril[];
}

export function CapacityDashboard({
  data,
  capacityType,
  openState,
  loading,
  filters,
  companyTeamsLookup,
  perils,
}: CapacityDashboardProps) {
  const parsedFilters = useFriendlyNameMapper(filters, perils || []);
  const ref = useGridApiRefKeepingPage<GridApiPremium>();
  const { field, virtual, claimOwner } = data?.totals || {};
  const nonClaimOwnerData = useFormatData(
    data?.capacityType !== 'ClaimOwner' ? data : [],
    companyTeamsLookup
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const exportValidationSchema = yup.object().shape({
    fileName: yup.string().required('File name is required'),
  });

  const { notifySuccess, notifyError } = useNotification();
  const user = useCurrentUser();
  const userId = String(user?.userId ?? '');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { fileName: '', format: ReportFormat.Excel },
    resolver: yupResolver(exportValidationSchema),
  });

  const onExportReport = (response: { success: boolean }) => {
    if (response.success) {
      notifySuccess('Success', 'Report job started');
    } else {
      notifyError('Error', 'Report not created, please try again');
    }

    setShowExportModal(false);
    reset();
  };

  const { exportReport, status: exportStatus } =
    useExportReport<CapacityDashboard_Request>(onExportReport);

  const onClickExport = ({
    fileName,
    format,
  }: {
    fileName: string;
    format: ReportFormat;
  }) => {
    exportReport({
      request: {
        userId,
        reportType: ReportType.Capacity,
        fileName,
        format,
        filterQuery: filters!, //force filters non null
      },
    });
  };

  //have to check use columns, where it is using it//************** */
  const { columns, columnVisibilityModel } = useColumns(
    capacityType,
    parsedFilters
  );
  const [expandAll, setExpandAll] = React.useState(false);

  const claimCapacityData = useMemo(() => {
    if (!data || data.capacityType !== 'ClaimOwner') return null;

    return data as z.infer<typeof CapacityResponseSchema>;
  }, [data]);

  function CustomLoadingOverlay() {
    return (
      <StyledGridOverlay>
        <CircularProgress value={loading ? 1 : 0} />
        <Box sx={{ mt: 2 }}>
          Analyzing resource allocation and capacity. Please wait while we
          generate your report.
        </Box>
      </StyledGridOverlay>
    );
  }

  const renderTotals = React.useCallback(() => {
    const type = (capacityType as string) || '';
    const additionalResourcesTotals = {
      Field: field?.additionalResourceCount || 0,
      Virtual: virtual?.additionalResourceCount || 0,
      ClaimOwner: claimOwner?.additionalResourceCount || 0,
    };
    const totals: Record<'Field' | 'ClaimOwner' | 'Virtual', any> = {
      Field: null,
      Virtual: null,
      ClaimOwner: null,
    };

    if (type !== 'ClaimOwner' && (type === 'Field' || type === 'Virtual')) {
      totals[type] = (
        <Grid size={4}>
          <Card variant="outlined" sx={{ padding: '8px !important' }}>
            <CardContent sx={{ padding: '0 !important' }}>
              <Typography
                gutterBottom
                sx={{ color: 'text.secondary', fontSize: 14 }}
              >
                {type}
              </Typography>
              <Stack justifyContent="center" alignItems="center">
                <Typography variant="h3" component="div">
                  {additionalResourcesTotals[type] || 0}
                </Typography>
                <Typography component="p" fontSize="10px">
                  Additional Resources
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      );
    }
    const orderTotals = [totals['Field'], totals['Virtual']];

    return orderTotals.map((total) => total);
  }, [capacityType, field, virtual, claimOwner]);

  const dateColumns: GridColDef[] = useMemo(() => {
    if (
      !claimCapacityData ||
      !claimCapacityData.data ||
      isEmpty(claimCapacityData.data)
    ) {
      return [];
    }

    return claimCapacityData?.data?.[0]?.dateData?.map((dateData) => {
      const field = format(parseISO(dateData.date), 'yyyy-MM-dd');
      const isToday = field === format(new Date(), 'yyyy-MM-dd');
      let header = format(parseISO(dateData.date), 'MM/dd');

      if (isToday) {
        header = 'Today';
      }

      return {
        field: field,
        type: 'string',
        sortable: false,
        maxWidth: 150,
        headerName: header,
        renderHeader: (params: any) => {
          if (isToday) {
            return (
              <Box sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {params.colDef.headerName}
              </Box>
            );
          }
          return params.colDef.headerName;
        },
        renderCell: (params: any) => {
          if (
            params.row[field].hoverData &&
            params.row[field].hoverData.length > 0
          ) {
            return (
              <ClaimOwnerToolTip
                dateData={params.row[field] || { info: '', hoverData: [] }}
                isToday={isToday}
              />
            );
          }

          return (
            <Box
              sx={isToday ? { fontWeight: 'bold', color: 'primary.main' } : {}}
            >
              {params.row[field].info || '0(0)'}
            </Box>
          );
        },
      };
    });
  }, [claimCapacityData]);

  const claimOwnerColumns: GridColDef[] = React.useMemo(() => {
    if (!data || data.capacityType !== 'ClaimOwner') return [];

    return [
      // this is needed as if we use name the header gets hidden, don't know why will have to figure it our later
      {
        field: 'resourceName',
        headerName: 'Adjuster',
        maxWidth: 200,
        type: 'string',
      },
      {
        field: 'COLocations',
        headerName: 'Locations',
        maxWidth: 100,
        sortable: false,
        type: 'string',
        renderCell: (params: any) => {
          const locations = (params?.row?.COLocations as Array<string>) || [];

          if (locations.length === 0) {
            return 'N/A';
          }

          const locationsString = locations.join(', ');

          if (locations.length <= 3) {
            return locationsString;
          }

          const locationArray = locations.slice(0, 3);

          locationArray.push('...');

          const shortLocationString = locationArray.join(', ');

          return (
            <Tooltip title={<Box>{locationsString}</Box>}>
              <Box>{shortLocationString}</Box>
            </Tooltip>
          );
        },
      },
      ...dateColumns,
    ];
  }, [dateColumns]);

  const renderColumns = React.useMemo(() => {
    if (filters?.capacityType === 'ClaimOwner') {
      return claimOwnerColumns;
    }

    return columns;
  }, [filters?.capacityType, columns, claimOwnerColumns]);

  const rows = React.useMemo(() => {
    if (claimOwnerColumns.length > 0) {
      if (claimCapacityData) {
        claimCapacityData.data.forEach((item) => {
          getDateData(item);
          // this is needed as if we use name the header gets hidden, don't know why will have to figure it our later
          item.resourceName = item.name;
        });

        return claimCapacityData.data;
      }

      return [];
    }

    return nonClaimOwnerData;
  }, [claimOwnerColumns.length, nonClaimOwnerData, claimCapacityData]);

  const renderDataGrid = useMemo(() => {
    if (filters?.capacityType === 'ClaimOwner') {
      return (
        <DataGridPremium
          apiRef={ref}
          sx={tableStyleHideSeparator}
          loading={loading}
          disableColumnMenu
          disableRowSelectionOnClick
          disableRowGrouping
          rows={rows || []}
          getRowId={(row) => row.rowId}
          columns={renderColumns}
          hideFooter
          hideFooterSelectedRowCount
        />
      );
    }

    return (
      <DataGridPremium
        apiRef={ref}
        sx={tableStyleHideSeparator}
        loading={loading}
        disableColumnFilter
        disableColumnSelector
        disableColumnMenu
        disableDensitySelector
        disableRowSelectionOnClick
        disableColumnSorting
        rows={rows || []}
        getRowId={(row) => row.rowId}
        columns={renderColumns}
        columnVisibilityModel={columnVisibilityModel}
        groupingColDef={groupingColDef}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
        }}
        columnBufferPx={100}
        columnGroupingModel={columnGroupingModel}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          activeResourcesFieldSum,
          activeResourcesClaimOwnerSum,
          activeResourcesVirtualSum,
          openSlotsSum,
          additionalResourcesSum,
          openSlotsSumClaimOwner,
          additionalResourcesClaimOwnerSum,
          openSlotsSumVirtual,
          additionalResourcesVirtualSum,
        }}
        initialState={{
          rowGrouping: {
            model: ['date', 'location'],
          },
          columns: {
            columnVisibilityModel: {
              date: false,
              location: false,
              externalResourceId: false,
            },
          },
          aggregation: {
            model: {
              activeResourcesField: 'activeResourcesFieldSum',
              openSlotsFieldResource: 'openSlotsSum',
              additionalResourcesField: 'additionalResourcesSum',
              activeResourcesClaimOwner: 'activeResourcesClaimOwnerSum',
              openSlotsClaimOwnerResource: 'openSlotsSumClaimOwner',
              additionalResourcesClaimOwner: 'additionalResourcesClaimOwnerSum',
              activeResourcesVirtual: 'activeResourcesVirtualSum',
              openSlotsVirtualResource: 'openSlotsSumVirtual',
              additionalResourcesVirtual: 'additionalResourcesVirtualSum',
            },
          },
        }}
        hideFooter
        hideFooterSelectedRowCount
        defaultGroupingExpansionDepth={expandAll ? -1 : undefined}
      />
    );
  }, [
    CustomLoadingOverlay,
    columnVisibilityModel,
    expandAll,
    filters?.capacityType,
    loading,
    ref,
    renderColumns,
    rows,
  ]);

  return (
    <PageComponent
      title="Capacity Dashboard"
      searchPlaceholder="Name"
      actions={[
        <Button
          sx={{ fontWeight: '700' }}
          variant="outlined"
          onClick={() => setShowExportModal(true)}
          disabled={exportStatus === 'loading'}
          startIcon={<FileDownload />}
        >
          Export
        </Button>,

        <Button
          variant={'outlined'}
          onClick={() => setExpandAll(!expandAll)}
          sx={{ fontWeight: '700' }}
        >
          {!expandAll ? 'Expand All' : 'Collapse All'}
        </Button>,
        <Button
          variant={'contained'}
          onClick={() => openState?.setOpen(true)}
          sx={{ fontWeight: '700' }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>Filter</div>
        </Button>,
      ]}
    >
      <>
        {showExportModal && (
          <ConfirmModal
            modalType={'export'}
            confirmTitle="Export Capacity Report"
            confirmContent={
              <Form<{ fileName: string; format: ReportFormat }>
                control={control}
                errors={errors}
                handleSubmit={handleSubmit}
                render={({ control, errors }: RenderFuncProps) => (
                  <Stack>
                    <div>
                      <TextField
                        id="fileName"
                        name="fileName"
                        label="File name:"
                        control={control}
                        errors={errors}
                        required
                      />
                      <SelectField
                        id="format"
                        name="format"
                        label="File Type"
                        control={control}
                        errors={errors}
                        defaultValue={ReportFormat.Excel}
                        required
                        options={[
                          {
                            title: ReportFormat.CSV,
                            value: ReportFormat.CSV,
                          },
                          {
                            title: ReportFormat.Excel,
                            value: ReportFormat.Excel,
                          },
                        ]}
                      />
                    </div>
                  </Stack>
                )}
              />
            }
            onConfirm={handleSubmit(onClickExport)}
            onDeny={() => {
              setShowExportModal(false);
              reset();
            }}
            confirmColor="primary"
          />
        )}
        {filters?.capacityType && filters?.capacityType !== 'ClaimOwner' && (
          <Grid container spacing={2} sx={{ marginBottom: 2, height: '114px' }}>
            {renderTotals()}
          </Grid>
        )}
        <Card>
          <Box
            sx={{
              [`.${gridClasses.cell}.grey`]: {
                backgroundColor: '#eeeeee',
              },
            }}
          >
            <div style={{ height: '75vh', width: '100%' }}>
              {renderDataGrid}
            </div>
          </Box>
        </Card>
      </>
    </PageComponent>
  );
}

export default CapacityDashboard;
